'use client';

import { useState } from 'react';
import Link from 'next/link';
import { ShoppingCart, Check, Package, Shield, Truck, ArrowLeft, ChevronLeft, ChevronRight, MessageCircle } from 'lucide-react';
import { getConditionLabel } from '@/lib/constants/products';
import ProductImage from '@/components/ProductImage';

export default function ProductDetail({ product }) {
  const [selectedImage, setSelectedImage] = useState(0);
  const [addingToCart, setAddingToCart] = useState(false);
  const [added, setAdded] = useState(false);
  const [showToast, setShowToast] = useState(false);
  const isSparePart = product.category === 'SPARE_PARTS';

  const images = JSON.parse(product.images);

  const handlePreviousImage = () => {
    setSelectedImage((prev) => (prev === 0 ? images.length - 1 : prev - 1));
  };

  const handleNextImage = () => {
    setSelectedImage((prev) => (prev === images.length - 1 ? 0 : prev + 1));
  };

  const handleAddToCart = async () => {
    setAddingToCart(true);

    try {
      const res = await fetch('/api/cart', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          productId: product.id,
          quantity: 1
        })
      });

      if (res.ok) {
        setAdded(true);
        setShowToast(true);
        setTimeout(() => {
          setAdded(false);
          setShowToast(false);
        }, 3000);
      } else {
        if (res.status === 401) {
          // Utilisateur non connecté
          alert('Vous devez créer un compte ou vous connecter pour ajouter des produits au panier.\n\nVous allez être redirigé vers la page de connexion.');
          window.location.href = `/login?redirect=/products/${product.slug}`;
        } else {
          const data = await res.json();
          alert(data.error || 'Erreur lors de l\'ajout au panier');
        }
      }
    } catch (error) {
      alert('Erreur lors de l\'ajout au panier');
    }

    setAddingToCart(false);
  };

  const discount = product.discount || 0;
  const savings = product.originalPrice ? product.originalPrice - product.price : 0;

  return (
    <div className="min-h-screen bg-gray-50">

      <div className="bg-white border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <Link
            href="/products"
            className="inline-flex items-center gap-2 text-gray-600 hover:text-gray-900 transition-colors"
          >
            <ArrowLeft size={20} />
            <span className="text-sm font-medium">Retour aux produits</span>
          </Link>
        </div>
      </div>


      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 lg:py-12">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-12">

          <div className="space-y-4">

            <div className="relative bg-white rounded-2xl overflow-hidden border border-gray-200 aspect-square flex items-center justify-center group">
              <ProductImage
                src={images[selectedImage]}
                alt={product.name}
                width={600}
                height={600}
                className="w-full h-full object-contain"
              />

              {/* Boutons de navigation (visibles seulement s'il y a plusieurs images) */}
              {images.length > 1 && (
                <>
                  <button
                    onClick={handlePreviousImage}
                    className="absolute left-2 sm:left-4 top-1/2 -translate-y-1/2 w-10 h-10 sm:w-12 sm:h-12 bg-white/90 hover:bg-white rounded-full flex items-center justify-center shadow-lg opacity-0 group-hover:opacity-100 transition-opacity duration-200 z-10"
                    aria-label="Image précédente"
                  >
                    <ChevronLeft size={24} className="text-gray-800" />
                  </button>
                  <button
                    onClick={handleNextImage}
                    className="absolute right-2 sm:right-4 top-1/2 -translate-y-1/2 w-10 h-10 sm:w-12 sm:h-12 bg-white/90 hover:bg-white rounded-full flex items-center justify-center shadow-lg opacity-0 group-hover:opacity-100 transition-opacity duration-200 z-10"
                    aria-label="Image suivante"
                  >
                    <ChevronRight size={24} className="text-gray-800" />
                  </button>

                  {/* Indicateur de position */}
                  <div className="absolute bottom-4 left-1/2 -translate-x-1/2 flex gap-2 z-10">
                    {images.map((_, index) => (
                      <button
                        key={index}
                        onClick={() => setSelectedImage(index)}
                        className={`w-2 h-2 rounded-full transition-all ${
                          selectedImage === index
                            ? 'bg-[#23994F] w-6'
                            : 'bg-gray-300 hover:bg-gray-400'
                        }`}
                        aria-label={`Aller à l'image ${index + 1}`}
                      />
                    ))}
                  </div>
                </>
              )}
            </div>


            {images.length > 1 && (
              <div className="grid grid-cols-4 gap-4">
                {images.map((img, index) => (
                  <button
                    key={index}
                    onClick={() => setSelectedImage(index)}
                    className={`bg-white rounded-lg overflow-hidden border-2 transition-all ${
                      selectedImage === index
                        ? 'border-[#23994F]'
                        : 'border-gray-200 hover:border-gray-300'
                    }`}
                  >
                    <ProductImage
                      src={img}
                      alt={`${product.name} ${index + 1}`}
                      width={150}
                      height={150}
                      className="w-full h-full object-contain"
                    />
                  </button>
                ))}
              </div>
            )}
          </div>


          <div className="space-y-6">

            <div>
              <p className="text-sm text-gray-600 mb-2">{product.brand}</p>
              <h1 className="text-3xl lg:text-4xl font-bold text-gray-900">
                {product.name}
              </h1>
            </div>


            {!isSparePart ? (
              <div className="bg-gray-100 rounded-xl p-6">
                <div className="flex items-baseline gap-3">
                  <span className="text-4xl font-bold text-gray-900">
                    {product.price}€
                  </span>
                  {product.originalPrice && (
                    <>
                      <span className="text-2xl text-gray-500 line-through">
                        {product.originalPrice}€
                      </span>
                      <span className="bg-red-500 text-white px-3 py-1 rounded-full text-sm font-bold">
                        -{discount}%
                      </span>
                    </>
                  )}
                </div>
                {savings > 0 && (
                  <p className="text-sm text-green-600 font-medium mt-2">
                    Économisez {savings.toFixed(2)}€
                  </p>
                )}
              </div>
            ) : (
              <div className="bg-blue-50 rounded-xl p-6 border-2 border-blue-200">
                <div className="flex items-center gap-3 mb-3">
                  <MessageCircle size={28} className="text-blue-600" />
                  <span className="text-2xl font-bold text-gray-900">
                    Prix sur devis
                  </span>
                </div>
                <p className="text-gray-600 text-sm">
                  Contactez-nous sur WhatsApp pour obtenir un devis personnalisé et vérifier la disponibilité.
                </p>
              </div>
            )}


            <div>
              {product.inStock ? (
                <div className="flex items-center gap-2 text-green-600">
                  <Check size={20} />
                  <span className="font-semibold">En stock ({product.stock} disponibles)</span>
                </div>
              ) : (
                <div className="flex items-center gap-2 text-red-600">
                  <span className="font-semibold">Rupture de stock</span>
                </div>
              )}
            </div>


            <div className="space-y-3 border-t border-b border-gray-200 py-6">
              {product.storage && (
                <div className="flex justify-between">
                  <span className="text-gray-600">Stockage</span>
                  <span className="font-semibold text-gray-900">{product.storage}</span>
                </div>
              )}
              {product.color && (
                <div className="flex justify-between">
                  <span className="text-gray-600">Couleur</span>
                  <span className="font-semibold text-gray-900">{product.color}</span>
                </div>
              )}
              {product.batteryHealth && (
                <div className="flex justify-between">
                  <span className="text-gray-600">Batterie</span>
                  <span className="font-semibold text-gray-900">{product.batteryHealth}%</span>
                </div>
              )}
              <div className="flex justify-between">
                <span className="text-gray-600">État</span>
                <span className="font-semibold text-gray-900">{getConditionLabel(product.condition)}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-600">Garantie</span>
                <span className="font-semibold text-gray-900">{product.warranty} mois</span>
              </div>
            </div>


            {isSparePart ? (
              <a
                href="https://wa.me/33651655554"
                target="_blank"
                rel="noopener noreferrer"
                className="w-full py-4 rounded-xl font-bold text-lg transition-all flex items-center justify-center gap-3 bg-green-500 hover:bg-green-600 text-white"
              >
                <MessageCircle size={24} />
                Demander un devis sur WhatsApp
              </a>
            ) : (
              <button
                onClick={handleAddToCart}
                disabled={!product.inStock || addingToCart || added}
                className={`w-full py-4 rounded-xl font-bold text-lg transition-all flex items-center justify-center gap-3 ${
                  added
                    ? 'bg-green-500 text-white'
                    : 'bg-[#23994F] hover:bg-[#1a7840] text-white disabled:bg-gray-300 disabled:cursor-not-allowed'
                }`}
              >
                {added ? (
                  <>
                    <Check size={24} />
                    Ajouté au panier !
                  </>
                ) : (
                  <>
                    <ShoppingCart size={24} />
                    {addingToCart ? 'Ajout en cours...' : 'Ajouter au panier'}
                  </>
                )}
              </button>
            )}


            <div className="grid grid-cols-3 gap-4 pt-4">
              <div className="text-center">
                <div className="w-12 h-12 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-2">
                  <Truck size={24} className="text-blue-600" />
                </div>
                <p className="text-xs text-gray-600">Livraison gratuite</p>
              </div>
              <div className="text-center">
                <div className="w-12 h-12 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-2">
                  <Shield size={24} className="text-green-600" />
                </div>
                <p className="text-xs text-gray-600">Garantie {product.warranty} mois</p>
              </div>
              <div className="text-center">
                <div className="w-12 h-12 bg-purple-100 rounded-full flex items-center justify-center mx-auto mb-2">
                  <Package size={24} className="text-purple-600" />
                </div>
                <p className="text-xs text-gray-600">Emballage sécurisé</p>
              </div>
            </div>


            {product.description && (
              <div className="pt-6 border-t border-gray-200">
                <h3 className="font-bold text-gray-900 mb-3">Description</h3>
                <p className="text-gray-600 leading-relaxed">{product.description}</p>
              </div>
            )}
          </div>
        </div>

        {showToast && (
          <div className="fixed bottom-8 right-8 z-50 animate-slide-in-right">
            <div className="bg-white rounded-xl shadow-2xl border border-gray-200 p-4 flex items-center gap-4 min-w-[320px]">
              <div className="w-12 h-12 bg-green-100 rounded-full flex items-center justify-center flex-shrink-0">
                <Check size={24} className="text-green-600" />
              </div>
              <div className="flex-1">
                <p className="font-bold text-gray-900 mb-1">Ajouté au panier !</p>
                <p className="text-sm text-gray-600">{product.name}</p>
              </div>
              <Link
                href="/cart"
                className="bg-[#23994F] hover:bg-[#1a7840] text-white px-4 py-2 rounded-lg text-sm font-semibold transition-colors"
              >
                Voir
              </Link>
            </div>
          </div>
        )}
      </div>

      <style jsx>{`
        @keyframes slide-in-right {
          from {
            transform: translateX(100%);
            opacity: 0;
          }
          to {
            transform: translateX(0);
            opacity: 1;
          }
        }
        .animate-slide-in-right {
          animation: slide-in-right 0.3s ease-out;
        }
      `}</style>
    </div>
  );
}
